/**
 * TESTWEISE. Erkennt Warenkorb-Adds an der Antwort der Cart API.
 *
 * Bisher entsteht add_to_cart aus einem Vergleich: beim Klick den Warenkorb
 * holen, eine Sekunde warten, nochmal holen, Differenz bilden. Das verliert
 * Events auf zwei Wegen. Liegt der Add vor dem ersten Snapshot, ist die
 * Differenz null. Dauert er laenger als die Sekunde, fehlt er im zweiten.
 * Beides trifft zufaellig, und zusammen kostet es rund vier von fuenf Events
 * (GA4 106 gegen 501 bei Shopify am 04.08.).
 *
 * Hier steht stattdessen die Antwort des Servers, die ist eindeutig:
 * - die MENGE kommt aus dem Request, dort steht, wie viel hinzugefuegt wird
 * - die PRODUKTDATEN kommen aus der Antwort
 * Der Umweg ueber den Request ist noetig, weil die Antwort von add.js laut
 * Cart API Doku die neue Gesamtmenge der Zeile enthaelt und nicht die
 * hinzugefuegte. Genau daran hing schon einmal ein Fehler ("4x" statt "1x").
 *
 * Mengenerhoehungen im Warenkorb laufen ueber change.js und update.js. Dort
 * steht im Request die absolute Zielmenge, also braucht es einen Vorzustand.
 * Der kommt aus dem letzten vom Server bestaetigten Warenkorb, nicht aus einem
 * Timer, und wird auf Variantenebene verglichen. Das ist unempfindlich dagegen,
 * dass sich der Zeilen-key aendert, sobald ein Rabatt greift.
 *
 * Laeuft bewusst als klassisches Script im Head: gtm-events.js ist ein Modul
 * und damit deferred, der Interceptor muss aber schon stehen, bevor
 * irgendein anderes Script den Warenkorb anfasst.
 *
 * Das Script pusht selbst nichts. Es meldet nur "es wurde etwas hinzugefuegt"
 * per Event jacks:cart-add. Was daraus wird, entscheidet gtm-events.js.
 */
(function () {
  'use strict';

  if (window.__jacksCartInterceptor) return;
  window.__jacksCartInterceptor = true;

  // Standardmaessig still. Zum Nachvollziehen in der Konsole einschalten:
  // window.__atcDebug = true, dann neu laden.
  function log(msg, data) {
    if (!window.__atcDebug) return;
    if (data !== undefined) console.log('[ATC] ' + msg, data);
    else console.log('[ATC] ' + msg);
  }

  function warn(msg, data) {
    if (!window.__atcDebug) return;
    if (data !== undefined) console.warn('[ATC] ' + msg, data);
    else console.warn('[ATC] ' + msg);
  }

  // Letzter vom Server bestaetigter Warenkorb als Index (siehe cartIndex).
  // Wird bei jeder Anfrage an change.js und update.js als Momentaufnahme
  // festgehalten, statt beim Eintreffen der Antwort gelesen zu werden: sonst
  // haben Fremdscripte, die parallel update.js aufrufen, den Stand laengst
  // ueberschrieben, und die Differenz waere null. Genau daran gingen im Test
  // zwei von vier Klicks auf Plus verloren.
  var lastCart = null;

  // Adds, die auflaufen, bevor gtm-events.js (Modul, deferred) bereitsteht.
  window.__jacksCartAddQueue = [];

  function emit(payload) {
    // Nur zwischenspeichern, solange der Empfaenger noch nicht haengt. Danach
    // reicht das Event, sonst waechst die Liste ueber die Session hinweg.
    if (!window.__jacksCartAddReady) window.__jacksCartAddQueue.push(payload);
    try {
      document.dispatchEvent(new CustomEvent('jacks:cart-add', { detail: payload }));
    } catch (e) {
      warn('Event konnte nicht ausgeloest werden', e);
    }
  }

  // Endpunkte erkennen. Locale-Praefixe wie /de/cart/add.js sind ueblich,
  // deshalb nur auf das Ende des Pfades pruefen.
  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url || '');
    }
  }

  function endpointOf(path) {
    if (/\/cart\/add(\.js)?$/.test(path)) return 'add';
    if (/\/cart\/change(\.js)?$/.test(path)) return 'change';
    if (/\/cart\/update(\.js)?$/.test(path)) return 'update';
    if (/\/cart\.js$/.test(path)) return 'cart';
    return null;
  }

  // --- Request lesen: wie viel wird hinzugefuegt? ---------------------------

  function normalizeRequestItem(raw) {
    var qty = parseInt(raw.quantity, 10);
    return {
      id: raw.id != null ? String(raw.id) : null,
      quantity: isNaN(qty) || qty <= 0 ? 1 : qty,
    };
  }

  // FormData und URLSearchParams. Beide Schreibweisen: einzeln (id, quantity)
  // und als Liste (items[0][id], items[0][quantity]).
  function itemsFromEntries(entries) {
    var single = { id: null, quantity: 1 };
    var bulk = {};

    entries.forEach(function (value, key) {
      var match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (match) {
        bulk[match[1]] = bulk[match[1]] || { id: null, quantity: 1 };
        bulk[match[1]][match[2]] = value;
        return;
      }
      if (key === 'id') single.id = value;
      if (key === 'quantity') single.quantity = value;
    });

    var list = Object.keys(bulk).map(function (index) {
      return normalizeRequestItem(bulk[index]);
    });
    if (list.length) return list;
    return single.id ? [normalizeRequestItem(single)] : [];
  }

  function itemsFromJson(obj) {
    if (!obj || typeof obj !== 'object') return [];
    if (Array.isArray(obj.items)) return obj.items.map(normalizeRequestItem);
    if (obj.id != null) return [normalizeRequestItem(obj)];
    return [];
  }

  function parseRequestBody(body) {
    try {
      if (!body) return [];
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return itemsFromEntries(body);
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return itemsFromEntries(body);
      }
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
          return itemsFromJson(JSON.parse(trimmed));
        }
        return itemsFromEntries(new URLSearchParams(trimmed));
      }
      if (typeof body === 'object') return itemsFromJson(body);
    } catch (e) {
      warn('Request-Body nicht lesbar, Menge kommt aus dem Vorzustand', e);
    }
    return [];
  }

  // --- Antwort lesen -------------------------------------------------------

  function itemsFromAddResponse(data) {
    if (!data || typeof data !== 'object') return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.id != null) return [data];
    return [];
  }

  function variantIdOf(item) {
    return String(item.variant_id != null ? item.variant_id : item.id);
  }

  // Warenkorb so aufbereiten, dass eine Zeile ueber alle Schreibweisen
  // auffindbar ist, die update.js und change.js zulassen: Zeilen-key,
  // Varianten-ID oder Position.
  function cartIndex(cart) {
    var items = (cart && cart.items) || [];
    var byKey = {};
    var byVariant = {};
    items.forEach(function (item) {
      if (item.key) byKey[String(item.key)] = item;
      var variantId = variantIdOf(item);
      if (!byVariant[variantId]) byVariant[variantId] = item;
    });
    return { items: items, byKey: byKey, byVariant: byVariant };
  }

  function findLine(index, id, positional) {
    if (!index) return null;
    if (positional) {
      var position = parseInt(id, 10);
      return index.items[position - 1] || null;
    }
    var key = String(id);
    return index.byKey[key] || index.byVariant[key] || null;
  }

  // Aus dem Request von change.js und update.js lesen, WELCHE Zeile auf WELCHE
  // Menge gesetzt wird. Das ist der Kern: nur wer selbst eine Menge schreibt,
  // hat etwas hinzugefuegt. Fremdscripte wie autoApplyDiscounts oder die
  // Stella-Empfehlungen rufen update.js mit leerem updates auf, um nur die
  // Sections neu zu holen. Die liefern hier eine leere Liste und damit kein
  // Event, obwohl ihre Antwort den kompletten Warenkorb enthaelt.
  function parseCartUpdateRequest(body, endpoint) {
    var out = [];
    try {
      if (!body) return out;

      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return updatesFromEntries(body, endpoint);
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return updatesFromEntries(body, endpoint);
      }

      var obj = null;
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{') obj = JSON.parse(trimmed);
        else return updatesFromEntries(new URLSearchParams(trimmed), endpoint);
      } else if (typeof body === 'object') {
        obj = body;
      }
      if (!obj) return out;

      if (endpoint === 'change') {
        if (obj.id != null && obj.quantity != null) {
          out.push({ id: String(obj.id), quantity: Number(obj.quantity), positional: false });
        } else if (obj.line != null && obj.quantity != null) {
          out.push({ id: String(obj.line), quantity: Number(obj.quantity), positional: true });
        }
        return out;
      }

      var updates = obj.updates;
      if (Array.isArray(updates)) {
        // Positionsliste: [3, 2, 1] setzt die Mengen der Zeilen der Reihe nach.
        updates.forEach(function (quantity, position) {
          out.push({ id: String(position + 1), quantity: Number(quantity), positional: true });
        });
      } else if (updates && typeof updates === 'object') {
        Object.keys(updates).forEach(function (key) {
          out.push({ id: String(key), quantity: Number(updates[key]), positional: false });
        });
      }
    } catch (e) {
      warn('Update-Body nicht lesbar', e);
    }
    return out;
  }

  function updatesFromEntries(entries, endpoint) {
    var out = [];
    var positionalCount = 0;
    var single = {};

    entries.forEach(function (value, key) {
      var named = key.match(/^updates\[(.+)\]$/);
      if (named) {
        out.push({ id: named[1], quantity: Number(value), positional: false });
        return;
      }
      if (key === 'updates[]') {
        out.push({ id: String(++positionalCount), quantity: Number(value), positional: true });
        return;
      }
      if (key === 'id') single.id = value;
      if (key === 'quantity') single.quantity = value;
    });

    if (!out.length && endpoint === 'change' && single.id != null && single.quantity != null) {
      out.push({ id: String(single.id), quantity: Number(single.quantity), positional: false });
    }
    return out;
  }

  // --- Die drei Faelle -----------------------------------------------------

  function handleAdd(requestItems, data) {
    var responseItems = itemsFromAddResponse(data);
    if (!responseItems.length) {
      warn('add.js gesehen, aber keine Zeilen in der Antwort', data);
      return;
    }

    var used = {};
    var result = responseItems.map(function (item) {
      var variantId = variantIdOf(item);
      var source = 'request';
      var quantity = null;

      // 1. Wahl: der Request. Dort steht die hinzugefuegte Menge.
      for (var i = 0; i < requestItems.length; i++) {
        if (!used[i] && requestItems[i].id === variantId) {
          used[i] = true;
          quantity = requestItems[i].quantity;
          break;
        }
      }

      // 2. Wahl: Differenz zum letzten bestaetigten Warenkorb.
      if (quantity === null && lastCart) {
        var beforeLine = findLine(lastCart, variantId, false);
        var before = beforeLine ? beforeLine.quantity : 0;
        var delta = item.quantity - before;
        if (delta > 0) {
          quantity = delta;
          source = 'vorzustand';
        }
      }

      // 3. Wahl: die Antwort selbst. Zu hoch, sobald die Zeile schon im
      // Warenkorb lag, deshalb nur als letzter Ausweg und sichtbar geloggt.
      if (quantity === null) {
        quantity = item.quantity;
        source = 'antwort (unsicher)';
      }

      return {
        item: item,
        quantityAdded: quantity,
        quantitySource: source,
      };
    });

    emit({ endpoint: 'add', lines: result });

    // Nach einem Add ist unser Vorzustand unvollstaendig: die Antwort enthaelt
    // nur die betroffenen Zeilen, Apps koennen aber weitere anfuegen (Gratis-
    // Zugaben). Ein stiller Nachschlag holt den echten Stand. Die Antwort
    // darauf laeuft als 'cart' durch und loest kein Event aus.
    resync();
  }

  var resyncTimer = null;
  function resync() {
    if (resyncTimer) clearTimeout(resyncTimer);
    resyncTimer = setTimeout(function () {
      resyncTimer = null;
      try {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(root + 'cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      } catch (e) {
        /* Vorzustand bleibt, wie er ist */
      }
    }, 500);
  }

  // Zuletzt gezaehlte Zielmenge je Zeile. Noetig, wenn mehrere Klicks kurz
  // hintereinander kommen: beide Anfragen tragen dann denselben Vorzustand,
  // und ohne diesen Merker wuerde der zweite Klick die Differenz doppelt
  // zaehlen. Wird auch beim Verringern fortgeschrieben, damit ein erneutes
  // Erhoehen danach wieder zaehlt.
  var countedTarget = {};

  function handleCartUpdate(requestBody, data, endpoint, snapshotAtRequest) {
    var updates = parseCartUpdateRequest(requestBody, endpoint);

    // Kein Mengen-Schreibvorgang. Das ist der Normalfall bei Fremdscripten,
    // die nur die Sections neu holen, und bewusst still.
    if (!updates.length) return;

    if (!snapshotAtRequest) {
      warn(endpoint + '.js: kein Vorzustand zum Zeitpunkt der Anfrage, uebersprungen');
      return;
    }

    var after = cartIndex(data);
    var lines = [];
    var unveraendert = [];

    updates.forEach(function (update) {
      if (isNaN(update.quantity)) return;

      var before = findLine(snapshotAtRequest, update.id, update.positional);
      var item = findLine(after, update.id, update.positional) || before;
      if (!item) {
        warn(endpoint + '.js: Zeile ' + update.id + ' nicht auffindbar');
        return;
      }

      var variantId = variantIdOf(item);
      var beforeQuantity = before ? before.quantity : 0;
      // Falls fuer diese Zeile schon eine hoehere Zielmenge gezaehlt wurde,
      // gilt die als Ausgangspunkt.
      if (countedTarget[variantId] > beforeQuantity) beforeQuantity = countedTarget[variantId];

      var delta = update.quantity - beforeQuantity;
      countedTarget[variantId] = update.quantity;

      if (delta > 0) {
        lines.push({
          item: item,
          quantityAdded: delta,
          quantitySource: 'request+vorzustand',
        });
      } else {
        unveraendert.push(item.product_title + ' ' + beforeQuantity + '->' + update.quantity);
      }
    });

    if (lines.length) {
      emit({ endpoint: endpoint, lines: lines });
    } else if (unveraendert.length) {
      // Verringern oder unveraendert. Kein Fehler, nur zur Nachvollziehbarkeit.
      log(endpoint + '.js: keine Zunahme (' + unveraendert.join(', ') + ')');
    }
  }

  function route(endpoint, requestBody, data, snapshotAtRequest) {
    if (!data) return;

    if (endpoint === 'add') {
      handleAdd(parseRequestBody(requestBody), data);
    } else if (endpoint === 'change' || endpoint === 'update') {
      handleCartUpdate(requestBody, data, endpoint, snapshotAtRequest);
    }

    // Jede Antwort mit vollstaendigem Warenkorb pflegt den Stand. Bei add.js
    // fehlen die unbeteiligten Zeilen, deshalb dort nur ueber den Nachschlag.
    if (endpoint !== 'add' && data.items) lastCart = cartIndex(data);
  }

  // --- fetch und XHR umhaengen ---------------------------------------------
  // Beide immer unveraendert durchreichen. Was hier passiert, darf den
  // Warenkorb unter keinen Umstaenden beeinflussen.

  var originalFetch = window.fetch;
  if (typeof originalFetch === 'function') {
    window.fetch = function (input, init) {
      var promise = originalFetch.apply(this, arguments);

      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var endpoint = endpointOf(pathOf(url));
        if (endpoint) {
          // Jetzt festhalten, nicht spaeter: lastCart wird durch ein neues
          // Objekt ersetzt, die Referenz bleibt also der Stand von hier.
          var snapshotAtRequest = lastCart;
          var bodyPromise;
          if (init && init.body != null) {
            bodyPromise = Promise.resolve(init.body);
          } else if (input && typeof input === 'object' && typeof input.clone === 'function') {
            try {
              bodyPromise = input.clone().text();
            } catch (e) {
              bodyPromise = Promise.resolve(null);
            }
          } else {
            bodyPromise = Promise.resolve(null);
          }

          promise
            .then(function (response) {
              if (!response || !response.ok) return;
              var clone;
              try {
                clone = response.clone();
              } catch (e) {
                return;
              }
              return Promise.all([
                bodyPromise.catch(function () {
                  return null;
                }),
                clone.json().catch(function () {
                  return null;
                }),
              ]).then(function (parts) {
                route(endpoint, parts[0], parts[1], snapshotAtRequest);
              });
            })
            .catch(function () {
              /* fehlgeschlagene Requests interessieren uns nicht */
            });
        }
      } catch (e) {
        warn('fetch-Beobachtung uebersprungen', e);
      }

      return promise;
    };
  }

  var originalOpen = XMLHttpRequest.prototype.open;
  var originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.__jacksEndpoint = endpointOf(pathOf(url));
    } catch (e) {
      this.__jacksEndpoint = null;
    }
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var xhr = this;
    try {
      if (xhr.__jacksEndpoint) {
        var snapshotAtRequest = lastCart;
        xhr.addEventListener('load', function () {
          try {
            if (xhr.status < 200 || xhr.status >= 300) return;
            var data = null;
            if (xhr.responseType === 'json') {
              data = xhr.response;
            } else if (xhr.responseType === '' || xhr.responseType === 'text') {
              data = JSON.parse(xhr.responseText);
            }
            route(xhr.__jacksEndpoint, body, data, snapshotAtRequest);
          } catch (e) {
            /* keine JSON-Antwort, nicht unser Fall */
          }
        });
      }
    } catch (e) {
      warn('XHR-Beobachtung uebersprungen', e);
    }
    return originalSend.apply(this, arguments);
  };

  // Vorzustand einmal holen, damit die erste Mengenerhoehung im Warenkorb
  // schon eine Vergleichsbasis hat.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resync);
  } else {
    resync();
  }

  log('aktiv, beobachtet cart/add.js, cart/change.js, cart/update.js');
})();
